import {
    getAllPoolItems,
    getAllPools,
    getFirmPools,
    getPool,
    getPoolChangeLog,
    getPoolItemPricing,
    getPoolItems,
    getSymbolPrices,
    simulatePoolPricing,
    simulatePricing,
} from './http/labPools';
import type { GetPoolChangeLogOptions, GetPoolItemsOptions } from './http/labPools';
import type { QueryObserverOptions } from '@tanstack/query-core';
import type {
    GetPoolChangeLogResponse,
    ListPoolItemsResponse,
    SymbolPricesResponse,
} from '@thinkalpha/locates-ws-client';
import type {
    PoolPricingSimulationForPoolRequest,
    PoolPricingSimulationRequest,
    PoolPricingSimulationResponse,
} from '@thinkalpha/ui-types/types/admin/lab-pool-pricing';
import type { GetPoolResponse, ListPoolsResponse } from '@thinkalpha/ui-types/types/admin/lab-pools';
import type { ReactBindings } from 'src/types/bindings';

/**
 * Read-only query factories and invalidation helpers for LAB
 * inventory pools. Kept separate from {@link ../labPoolsMutations}
 * so read-only consumers do not transitively pull in the write
 * code paths.
 */

export const LAB_POOLS = 'lab-pools';
export const LAB_POOL = 'lab-pool';
export const LAB_POOL_ITEMS = 'lab-pool-items';
export const LAB_POOL_CHANGELOG = 'lab-pool-changelog';
export const LAB_POOL_ITEM_PRICING = 'lab-pool-item-pricing';
export const LAB_POOL_PRICING_SIMULATION = 'lab-pool-pricing-simulation';
export const LAB_PRICING_SIMULATION = 'lab-pricing-simulation';
export const LAB_SYMBOL_PRICES = 'lab-symbol-prices';

/**
 * 30s `staleTime` for both the pool list and per-pool items. Pool
 * membership and inventory both change at most every few seconds via
 * the locates-ws apply pipeline; without a `staleTime`, every focus
 * event or remount triggers a refetch and the table flickers.
 * Mutations explicitly call `invalidatePoolsQuery` /
 * `invalidatePoolItemsQuery`, so a short stale window is safe.
 */
const STALE_TIME_MS = 30_000;

/**
 * Lists all pools the caller has at least `reader` access to. The
 * backend gates the result-set on `effectiveAccess`; superuser callers
 * see every pool, firm-scoped callers only theirs. Use
 * {@link getFirmPoolsQuery} when you already know the target firm.
 */
export const getAllPoolsQuery = (): QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse> => {
    return {
        queryKey: [LAB_POOLS],
        queryFn: () => getAllPools(),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Lists pools scoped to a single firm. Accepts `undefined` to
 * support React-Query's standard "conditional fetch" pattern; the
 * query stays disabled until a firmId is provided.
 */
export const getFirmPoolsQuery = (
    firmId: string | undefined,
): QueryObserverOptions<ListPoolsResponse, Error, ListPoolsResponse> => {
    return {
        queryKey: [LAB_POOLS, firmId],
        queryFn: () => getFirmPools(firmId!),
        enabled: Boolean(firmId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Inspect a single pool by id. Useful when the caller has a poolId
 * (e.g. from a deep link) but no list result to source the metadata
 * from.
 */
export const getPoolQuery = (
    poolId: string | undefined,
): QueryObserverOptions<GetPoolResponse, Error, GetPoolResponse> => {
    return {
        queryKey: [LAB_POOL, poolId],
        queryFn: () => getPool(poolId!),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Lists inventory items for a single pool. Page params (`skip`,
 * `limit`) and the optional exact-match `symbol` filter are all part
 * of the cache key so distinct page/filter slices don't clobber each
 * other and the per-page fetches can be invalidated together via the
 * `[LAB_POOL_ITEMS, poolId]` prefix.
 */
export const getPoolItemsQuery = (
    poolId: string | undefined,
    options: GetPoolItemsOptions = {},
): QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse> => {
    const { symbol, skip, limit } = options;
    return {
        queryKey: [LAB_POOL_ITEMS, poolId, symbol ?? null, skip ?? null, limit ?? null],
        queryFn: () => getPoolItems(poolId!, { symbol, skip, limit }),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Loads a pool's entire inventory via real backend pagination. Backs
 * the "all rows" table mode and the CSV export; both share this cache
 * entry. The key is distinct from the paged / symbol-filtered slices
 * but still under the `[LAB_POOL_ITEMS, poolId]` prefix so it is
 * invalidated alongside them.
 */
export const getAllPoolItemsQuery = (
    poolId: string | undefined,
): QueryObserverOptions<ListPoolItemsResponse, Error, ListPoolItemsResponse> => {
    return {
        queryKey: [LAB_POOL_ITEMS, poolId, 'all'],
        queryFn: () => getAllPoolItems(poolId!),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Page size used when gathering a pool's entire change log by real
 * backend pagination (the "all" rows-per-page mode). Mirrors the items
 * all-rows fetch; the endpoint pages with `skip`/`limit` only.
 */
export const CHANGE_LOG_PAGE_SIZE = 500;

/**
 * Upper bound on in-flight change-log page requests when gathering the whole
 * log, mirroring the items all-rows fetch's cap so a large log doesn't fire
 * hundreds of concurrent requests over HTTP/2.
 */
export const CHANGE_LOG_MAX_CONCURRENT_PAGES = 8;

/**
 * Fetches every change-log entry for a pool by paging the backend with
 * real `skip`/`limit` requests until the reported `count` is reached,
 * rather than guessing a single oversized `limit`. The first page reports
 * the total; the rest are fetched in bounded concurrent batches and
 * concatenated in order (newest-first, as the backend returns them).
 */
async function fetchAllPoolChangeLog(poolId: string, filter: string | undefined): Promise<GetPoolChangeLogResponse> {
    const first = await getPoolChangeLog(poolId, { skip: 0, limit: CHANGE_LOG_PAGE_SIZE, filter });
    const { count } = first;
    const totalPages = Math.ceil(count / CHANGE_LOG_PAGE_SIZE);
    if (totalPages <= 1) return { entries: first.entries, count };
    const rest: GetPoolChangeLogResponse[] = [];
    for (let page = 1; page < totalPages; page += CHANGE_LOG_MAX_CONCURRENT_PAGES) {
        const batchSize = Math.min(CHANGE_LOG_MAX_CONCURRENT_PAGES, totalPages - page);
        const batch = await Promise.all(
            Array.from({ length: batchSize }, (_, i) =>
                getPoolChangeLog(poolId, {
                    skip: (page + i) * CHANGE_LOG_PAGE_SIZE,
                    limit: CHANGE_LOG_PAGE_SIZE,
                    filter,
                }),
            ),
        );
        rest.push(...batch);
    }
    return { entries: [first.entries, ...rest.map((page) => page.entries)].flat(), count };
}

/**
 * Loads a pool's entire change log via real backend pagination. Backs
 * the "all" rows-per-page mode; virtualization keeps the DOM flat. The
 * OData `$filter` participates in the cache key so distinct filters don't
 * clobber each other; the `[LAB_POOL_CHANGELOG, poolId]` prefix invalidates
 * every cached page and filter at once.
 */
export const getAllPoolChangeLogQuery = (
    poolId: string | undefined,
    options: Pick<GetPoolChangeLogOptions, 'filter'> = {},
): QueryObserverOptions<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse> => {
    const { filter } = options;
    return {
        queryKey: [LAB_POOL_CHANGELOG, poolId, 'all', filter ?? null],
        queryFn: () => fetchAllPoolChangeLog(poolId!, filter),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Pages a pool's audit-trail entries. `skip`/`limit` and the OData
 * `$filter` participate in the cache key so distinct pages / filters don't
 * clobber each other; the `[LAB_POOL_CHANGELOG, poolId]` prefix invalidates
 * them all at once.
 */
export const getPoolChangeLogQuery = (
    poolId: string | undefined,
    options: GetPoolChangeLogOptions = {},
): QueryObserverOptions<GetPoolChangeLogResponse, Error, GetPoolChangeLogResponse> => {
    const { skip, limit, filter } = options;
    return {
        queryKey: [LAB_POOL_CHANGELOG, poolId, skip ?? null, limit ?? null, filter ?? null],
        queryFn: () => getPoolChangeLog(poolId!, { skip, limit, filter }),
        enabled: Boolean(poolId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * The pool's active pricing for one of its items (the sidecard's data).
 * Keyed per (pool, item), so identical hovers within the stale window are
 * served from the cache and concurrent requests for the same row dedupe —
 * the sidecard opens instantly on re-hover instead of re-requesting.
 * Requires `reader` on the pool.
 */
export const getPoolItemPricingQuery = (
    poolId: string,
    itemId: string,
): QueryObserverOptions<PoolPricingSimulationResponse, Error, PoolPricingSimulationResponse> => {
    return {
        queryKey: [LAB_POOL_ITEM_PRICING, poolId, itemId],
        queryFn: () => getPoolItemPricing(poolId, itemId),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Simulation against the pool's SAVED pricing config (requires `reader`).
 * The request rides the query key so identical simulator runs within the
 * stale window are served from the cache.
 */
export const getPoolPricingSimulationQuery = (
    poolId: string,
    request: PoolPricingSimulationForPoolRequest,
): QueryObserverOptions<PoolPricingSimulationResponse, Error, PoolPricingSimulationResponse> => {
    return {
        queryKey: [LAB_POOL_PRICING_SIMULATION, poolId, request],
        queryFn: () => simulatePoolPricing(poolId, request),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Simulation against an arbitrary (draft) config — superuser-only on the
 * backend; the Info tab simulator uses it to preview unsaved edits.
 */
export const getPricingSimulationQuery = (
    request: PoolPricingSimulationRequest,
): QueryObserverOptions<PoolPricingSimulationResponse, Error, PoolPricingSimulationResponse> => {
    return {
        queryKey: [LAB_PRICING_SIMULATION, request],
        queryFn: () => simulatePricing(request),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * The locate-price index for a symbol (every level + contributing
 * providers). Config-independent, so the cache keys purely on the symbol;
 * re-opening the price summary within the stale window is served from the
 * cache. The endpoint currently rejects non-superusers with a 403 but is
 * being opened to firm admins and owners; the UI gates on
 * `canViewPricingResolution` and surfaces rollout 403s as error toasts.
 */
export const getSymbolPricesQuery = (
    symbol: string,
): QueryObserverOptions<SymbolPricesResponse, Error, SymbolPricesResponse> => {
    return {
        queryKey: [LAB_SYMBOL_PRICES, symbol],
        queryFn: () => getSymbolPrices(symbol),
        staleTime: STALE_TIME_MS,
    };
};

/**
 * Invalidate every cached page of a pool's change-log, or the whole
 * `LAB_POOL_CHANGELOG` namespace when no poolId is provided.
 */
export const invalidatePoolChangeLogQuery = (
    queryClient: ReactBindings['QueryClient'],
    poolId: string | undefined = undefined,
) => {
    return poolId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOL_CHANGELOG, poolId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOL_CHANGELOG] });
};

/**
 * Invalidate the pool-list query. Pass a firmId to scope the
 * invalidation; omit to clear all pool-list cache entries.
 */
export const invalidatePoolsQuery = (
    queryClient: ReactBindings['QueryClient'],
    firmId: string | undefined = undefined,
) => {
    return firmId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOLS, firmId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOLS] });
};

/**
 * Invalidate one pool's items, or all item caches if no poolId.
 * The match is prefix-based so both unfiltered and symbol-filtered
 * caches for a given pool are invalidated together.
 */
export const invalidatePoolItemsQuery = (
    queryClient: ReactBindings['QueryClient'],
    poolId: string | undefined = undefined,
) => {
    return poolId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOL_ITEMS, poolId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOL_ITEMS] });
};

/**
 * Invalidate the single-pool inspect query, or all single-pool
 * cache entries if no poolId.
 */
export const invalidatePoolQuery = (
    queryClient: ReactBindings['QueryClient'],
    poolId: string | undefined = undefined,
) => {
    return poolId
        ? queryClient.invalidateQueries({ queryKey: [LAB_POOL, poolId] })
        : queryClient.invalidateQueries({ queryKey: [LAB_POOL] });
};
